import React, { useState } from 'react';
import { Box, Typography, Divider, Badge, LinearProgress, Tooltip, Avatar, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import GradientText from '../../components/common/GradientText.tsx';
//import { getAvatarComponent } from '../../utils/getAvatarComponent.tsx';
import { 
  ContributionType, 
  BadgeType, 
  BadgeLevel, 
  BadgeCategory, 
  BADGE_COLORS,
  UserBadge as IUserBadge,
  UserReputation as IUserReputation
} from '../../services/attributionService.ts';

// Mock data for testing
const mockUserReputation: IUserReputation = {
  totalPoints: 1250,
  level: 3,
  rank: 42,
  percentile: 92,
  contributions: [
    {
      id: '1',
      type: ContributionType.FOUNDATIONAL,
      title: 'AI-Powered Customer Service Platform',
      date: '2023-11-15',
      points: 250,
      metrics: {
        complexity: 0.8,
        impact: 0.9,
        novelty: 0.7,
        synergy: 0.6
      }
    },
    {
      id: '2',
      type: ContributionType.APPLIED,
      title: 'Predictive Maintenance System',
      date: '2023-12-05',
      points: 180,
      metrics: {
        complexity: 0.7,
        impact: 0.8,
        novelty: 0.6,
        synergy: 0.5
      }
    },
    {
      id: '3',
      type: ContributionType.FUNCTIONAL,
      title: 'Enhanced RAG Implementation',
      date: '2024-01-10',
      points: 120,
      metrics: {
        complexity: 0.6,
        impact: 0.7,
        novelty: 0.8,
        synergy: 0.7
      }
    },
    {
      id: '4',
      type: ContributionType.EMERGENT,
      title: 'Autonomous Decision System',
      date: '2024-02-15',
      points: 300,
      metrics: {
        complexity: 0.9,
        impact: 0.95,
        novelty: 0.85,
        synergy: 0.9
      }
    }
  ],
  badges: [
    {
      id: '1',
      type: BadgeType.PROBLEM_SOLVER,
      title: 'Problem Solver',
      description: 'Solved 10+ problems with high-quality solutions',
      icon: 'Alpha',
      dateEarned: '2023-11-20',
      level: BadgeLevel.GOLD,
      category: BadgeCategory.STANDARD,
      progress: 65,
      requirements: ['Solve 15 problems to reach Platinum']
    },
    {
      id: '2',
      type: BadgeType.TOP_CONTRIBUTOR,
      title: 'Top Contributor',
      description: 'Ranked in the top 10% of contributors',
      icon: 'Beta',
      dateEarned: '2023-12-15',
      level: BadgeLevel.SILVER,
      category: BadgeCategory.STANDARD,
      progress: 40,
      requirements: ['Reach top 3% to achieve Gold level']
    },
    {
      id: '3',
      type: BadgeType.AI_INNOVATOR,
      title: 'AI Innovator',
      description: 'Created innovative AI solutions',
      icon: 'Gamma',
      dateEarned: '2024-01-05',
      level: BadgeLevel.BRONZE,
      category: BadgeCategory.STANDARD,
      progress: 25,
      requirements: ['Create 5 more AI solutions to reach Silver']
    },
    {
      id: '4',
      type: BadgeType.VISIONARY,
      title: 'Visionary',
      description: 'Proposed groundbreaking ideas',
      icon: 'Delta',
      dateEarned: '2024-02-10',
      level: BadgeLevel.SILVER,
      category: BadgeCategory.STANDARD,
      progress: 30,
      requirements: ['Have 5 more ideas implemented to reach Gold']
    },
    {
      id: '5',
      type: BadgeType.TECHNICAL_EXPERT,
      title: 'Technical Expert',
      description: 'Demonstrated deep technical knowledge',
      icon: 'Alpha',
      dateEarned: '2024-03-15',
      level: BadgeLevel.GOLD,
      category: BadgeCategory.STANDARD,
      progress: 50,
      requirements: ['Complete 3 more complex implementations to reach Platinum']
    },
    {
      id: '6',
      type: BadgeType.COMMUNITY_BUILDER,
      title: 'Community Builder',
      description: 'Helped grow the Eterna community',
      icon: 'Beta',
      dateEarned: '2024-04-20',
      level: BadgeLevel.BRONZE,
      category: BadgeCategory.STANDARD,
      progress: 20,
      requirements: ['Help 10 more users to reach Silver']
    },
    {
      id: '7',
      type: BadgeType.PIONEER,
      title: 'Pioneer',
      description: 'First to explore new technological frontiers',
      icon: 'Eterna',
      dateEarned: '2024-03-01',
      level: BadgeLevel.LEGENDARY,
      category: BadgeCategory.SPECIAL,
      requirements: ['Awarded for being the first to implement a revolutionary concept']
    }
  ]
};

// Styled components
const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(2),
}));

const BadgeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const StyledBadge = styled(Box)<{ level: BadgeLevel; category?: BadgeCategory }>(({ theme, level, category }) => {
  // Get the badge color from the BADGE_COLORS mapping
  const color = BADGE_COLORS[level] || BADGE_COLORS[BadgeLevel.BRONZE];
  
  // Special styling for different badge categories
  const isSpecial = category === BadgeCategory.SPECIAL;
  const isLimited = category === BadgeCategory.LIMITED;
  const isLegendary = level === BadgeLevel.LEGENDARY;
  
  return {
    position: 'relative',
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: isLegendary ? 
      'linear-gradient(135deg, rgba(26, 26, 46, 0.8), rgba(255, 69, 0, 0.2))' : 
      'rgba(26, 26, 46, 0.8)',
    border: `2px solid ${color}`,
    boxShadow: isSpecial || isLegendary ? 
      `0 0 15px ${color}, 0 0 5px ${color}` : 
      `0 0 10px ${color}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.1)',
      boxShadow: isSpecial || isLegendary ? 
        `0 0 20px ${color}, 0 0 10px ${color}` : 
        `0 0 15px ${color}`,
    },
    '&::after': isLimited ? {
      content: '""',
      position: 'absolute',
      top: -5,
      right: -5,
      width: 15,
      height: 15,
      borderRadius: '50%',
      backgroundColor: '#FF4500',
      border: '2px solid #FFD700',
    } : {},
  };
});

const BadgeProgressBar = styled(LinearProgress)<{ level: BadgeLevel }>(({ theme, level }) => {
  const color = BADGE_COLORS[level] || BADGE_COLORS[BadgeLevel.BRONZE];
  
  return {
    height: 4,
    width: '100%',
    borderRadius: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    marginTop: 2,
    '& .MuiLinearProgress-bar': {
      backgroundColor: color,
    },
  };
});

const BadgeCategoryChip = styled(Chip)<{ category: BadgeCategory }>(({ theme, category }) => {
  const colors = {
    [BadgeCategory.STANDARD]: '#8A2BE2',
    [BadgeCategory.SPECIAL]: '#FF4500',
    [BadgeCategory.LIMITED]: '#FFD700',
    [BadgeCategory.ACHIEVEMENT]: '#00CC99',
  };
  
  return {
    height: 20,
    fontSize: '0.6rem',
    backgroundColor: `${colors[category]}20`,
    color: colors[category],
    border: `1px solid ${colors[category]}40`,
    marginTop: 4,
  };
});

const ProgressContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const ContributionItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1),
  background: 'rgba(138, 43, 226, 0.1)',
  '&:hover': {
    background: 'rgba(138, 43, 226, 0.2)',
  },
}));

const FilterButton = styled(Box)<{ active: boolean; type: ContributionType }>(({ theme, active, type }) => {
  const colors = {
    [ContributionType.FOUNDATIONAL]: '#8A2BE2', // Purple
    [ContributionType.FUNCTIONAL]: '#00CC99', // Teal
    [ContributionType.APPLIED]: '#FF6F00', // Orange
    [ContributionType.EMERGENT]: '#003366', // Dark Blue
  };
  
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0.5, 0.8),
    borderRadius: '12px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    backgroundColor: active ? `${colors[type]}40` : 'rgba(26, 26, 46, 0.5)',
    color: active ? colors[type] : 'rgba(255, 255, 255, 0.7)',
    border: `1px solid ${active ? colors[type] : 'rgba(255, 255, 255, 0.2)'}`,
    transition: 'all 0.3s ease',
    minWidth: '28px',
    height: '28px',
    '&:hover': {
      backgroundColor: `${colors[type]}20`,
      color: colors[type],
      transform: 'translateY(-2px)',
      boxShadow: `0 3px 5px rgba(0, 0, 0, 0.2)`,
    },
  };
});

const UserAvatarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 64,
  height: 64,
  border: '2px solid rgba(138, 43, 226, 0.5)',
  boxShadow: '0 0 15px rgba(138, 43, 226, 0.3)',
}));

const NotificationBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#00CC99',
    color: '#fff',
    fontWeight: 'bold',
  },
}));

const RoundedImage = styled('img')({
  borderRadius: '50%',
  objectFit: 'cover'
});

// Get color for contribution type
const getContributionTypeColor = (type: ContributionType): string => {
  switch (type) {
    case ContributionType.FOUNDATIONAL:
      return '#8A2BE2'; // Purple
    case ContributionType.FUNCTIONAL:
      return '#00CC99'; // Teal
    case ContributionType.APPLIED:
      return '#FF6F00'; // Orange
    case ContributionType.EMERGENT:
      return '#003366'; // Dark Blue
    default:
      return '#8A2BE2';
  }
};

// Get badge icon component with appropriate styling based on level and category
const getBadgeIcon = (badge: IUserBadge): React.ReactNode => {
  const isLegendary = badge.level === BadgeLevel.LEGENDARY;
  const size = isLegendary ? 36 : 26;
  
  // Use relevant image based on badge type
  let imgSrc = "/images/leaf.png";
  
  switch (badge.type) {
    case BadgeType.PROBLEM_SOLVER:
      imgSrc = "/images/play.png";
      break;
    case BadgeType.TOP_CONTRIBUTOR:
      imgSrc = "/images/threedots.png";
      break;
    case BadgeType.AI_INNOVATOR:
      imgSrc = "/images/bot.png";
      break;
    case BadgeType.VISIONARY:
      imgSrc = "/images/white.png";
      break;
    case BadgeType.TECHNICAL_EXPERT:
      imgSrc = "/images/up.png";
      break;
    case BadgeType.COMMUNITY_BUILDER:
      imgSrc = "/images/together.png";
      break;
    case BadgeType.PIONEER:
      imgSrc = "/images/leaf.png";
      break;
  }
  
  return <RoundedImage 
    src={imgSrc} 
    alt={badge.title} 
    width={size} 
    height={size} 
  />;
};

// Get badge level name
const getBadgeLevelName = (level: BadgeLevel): string => {
  switch (level) {
    case BadgeLevel.BRONZE: return 'Bronze';
    case BadgeLevel.SILVER: return 'Silver';
    case BadgeLevel.GOLD: return 'Gold';
    case BadgeLevel.PLATINUM: return 'Platinum';
    case BadgeLevel.DIAMOND: return 'Diamond';
    case BadgeLevel.LEGENDARY: return 'Legendary';
    default: return 'Unknown';
  }
};

const UserContributionSidebar: React.FC = () => {
  const userReputation = mockUserReputation; // In a real app, this would come from an API
  const [selectedType, setSelectedType] = useState<ContributionType | null>(null);
  const [selectedBadgeCategory, setSelectedBadgeCategory] = useState<BadgeCategory | null>(null);
  
  // Calculate level progress (0-100)
  const levelProgress = (userReputation.totalPoints % 500) / 5; // Assuming 500 points per level
  
  // Filter contributions by type if a type is selected
  const filteredContributions = selectedType
    ? userReputation.contributions.filter(contribution => contribution.type === selectedType)
    : userReputation.contributions;
  
  // Filter badges by category if a category is selected
  const filteredBadges = selectedBadgeCategory
    ? userReputation.badges.filter(badge => badge.category === selectedBadgeCategory)
    : userReputation.badges;
  
  // Count badges by level
  const badgeCounts = userReputation.badges.reduce((acc, badge) => {
    acc[badge.level] = (acc[badge.level] || 0) + 1;
    return acc;
  }, {} as Record<BadgeLevel, number>);
  
  return (
    <Box sx={{ width: '100%', maxHeight: '100vh', overflowY: 'auto' }}>
      {/* User Avatar Section */}
      <Container>
        <UserAvatarContainer>
          <NotificationBadge badgeContent={3} overlap="circular">
            <StyledAvatar>
              <RoundedImage 
                src="/images/human.png" 
                alt="User Avatar" 
                width={98} 
                height={98} 
              />
            </StyledAvatar>
          </NotificationBadge>
          <Box>
            <Typography variant="h6" sx={{ color: '#fff' }}>
              John Doe
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              AI Researcher
            </Typography>
          </Box>
        </UserAvatarContainer>
        <Divider sx={{ borderColor: 'rgba(138, 43, 226, 0.2)', my: 2 }} />
        <GradientText variant="h6" gradient="primary" sx={{ mb: 1 }}>
          Your Reputation
        </GradientText>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Level {userReputation.level}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {userReputation.totalPoints} points
          </Typography>
        </Box>
        
        <ProgressContainer>
          <LinearProgress 
            variant="determinate" 
            value={levelProgress} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: 'rgba(138, 43, 226, 0.2)',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #8A2BE2, #00CC99)',
                borderRadius: 4,
              }
            }} 
          />
        </ProgressContainer>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Rank #{userReputation.rank}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Top {userReputation.percentile}%
          </Typography>
        </Box>
      </Container>
      
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <GradientText variant="h6" gradient="primary">
            Your Badges
          </GradientText>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: 0.5, 
            flexWrap: 'wrap',
            maxWidth: '120px' // Limit the width
          }}>
            {Object.values(BadgeCategory).map((category) => (
              <FilterButton 
                key={category}
                type={ContributionType.FOUNDATIONAL} // Just for styling
                active={selectedBadgeCategory === category}
                onClick={() => setSelectedBadgeCategory(selectedBadgeCategory === category ? null : category)}
              >
                {category.charAt(0).toUpperCase()}
              </FilterButton>
            ))}
          </Box>
        </Box>
        
        {/* Badge level summary */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {Object.entries(badgeCounts).map(([level, count]) => (
            <Chip
              key={level}
              icon={<RoundedImage src="/images/trophy.png" alt="Badge Level" width={16} height={16} />}
              label={`${getBadgeLevelName(Number(level) as BadgeLevel)}: ${count}`}
              size="small"
              sx={{
                backgroundColor: `${BADGE_COLORS[Number(level) as BadgeLevel]}20`,
                color: BADGE_COLORS[Number(level) as BadgeLevel],
                border: `1px solid ${BADGE_COLORS[Number(level) as BadgeLevel]}40`,
                fontSize: '0.7rem',
              }}
            />
          ))}
        </Box>
        
        <BadgeContainer>
          {filteredBadges.map((badge) => (
            <Tooltip 
              key={badge.id} 
              title={
                <React.Fragment>
                  <Typography variant="subtitle2">
                    {badge.title} ({getBadgeLevelName(badge.level)})
                  </Typography>
                  <Typography variant="body2">{badge.description}</Typography>
                  <Typography variant="caption">
                    Earned on {new Date(badge.dateEarned).toLocaleDateString()}
                  </Typography>
                  {badge.progress !== undefined && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        Progress to next level: {badge.progress}%
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={badge.progress} 
                        sx={{ 
                          height: 4, 
                          borderRadius: 2,
                          mt: 0.5,
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        }} 
                      />
                    </Box>
                  )}
                  {badge.requirements && badge.requirements.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        Requirements:
                      </Typography>
                      {badge.requirements.map((req, index) => (
                        <Typography key={index} variant="caption" sx={{ display: 'block', ml: 1 }}>
                          • {req}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </React.Fragment>
              }
              arrow
              placement="top"
              sx={{ maxWidth: 300 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <StyledBadge level={badge.level} category={badge.category}>
                  {getBadgeIcon(badge)}
                </StyledBadge>
                {badge.progress !== undefined && (
                  <BadgeProgressBar 
                    variant="determinate" 
                    value={badge.progress} 
                    level={badge.level}
                  />
                )}
                {badge.category && badge.category !== BadgeCategory.STANDARD && (
                  <BadgeCategoryChip 
                    label={badge.category.charAt(0).toUpperCase() + badge.category.slice(1)} 
                    size="small"
                    category={badge.category}
                  />
                )}
              </Box>
            </Tooltip>
          ))}
        </BadgeContainer>
      </Container>
      
      <Container>
        <Box sx={{ 
          display: 'flex', 
          gap: 0.5, 
          flexWrap: 'wrap', 
          alignItems: 'center',
          backgroundColor: 'rgba(26, 26, 46, 0.3)',
          borderRadius: '14px',
          padding: '4px 8px',
          border: '1px solid rgba(138, 43, 226, 0.1)'
        }}>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', mr: 1 }}>
            Filter: Foundational; Functional; Applied; Emergent
          </Typography>
          {/* Filter buttons here */}
        </Box>
      </Container>
      
      <Container>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2,
          flexWrap: 'wrap'
        }}>
          <GradientText variant="h6" gradient="primary" sx={{ mb: { xs: 1, sm: 0 } }}>
            Recent Contributions
          </GradientText>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 0.5, 
            flexWrap: 'wrap', 
            justifyContent: 'flex-end',
            backgroundColor: 'rgba(26, 26, 46, 0.3)',
            borderRadius: '14px',
            padding: '4px',
            border: '1px solid rgba(138, 43, 226, 0.1)'
          }}>
            {Object.values(ContributionType).map((type) => (
              <FilterButton 
                key={type}
                type={type as ContributionType}
                active={selectedType === type}
                onClick={() => setSelectedType(selectedType === type ? null : type as ContributionType)}
              >
                {type === ContributionType.FOUNDATIONAL && (
                  <RoundedImage 
                    src="/images/leaf.png" 
                    alt="Foundational" 
                    width={16} 
                    height={16} 
                    sx={{ marginRight: 4 }} 
                  />
                )}
                {type === ContributionType.FUNCTIONAL && (
                  <RoundedImage 
                    src="/images/play2.png" 
                    alt="Functional" 
                    width={16} 
                    height={16} 
                    sx={{ marginRight: 4 }} 
                  />
                )}
                {type === ContributionType.APPLIED && (
                  <RoundedImage 
                    src="/images/star.png" 
                    alt="Applied" 
                    width={16} 
                    height={16} 
                    sx={{ marginRight: 4 }} 
                  />
                )}
                {type === ContributionType.EMERGENT && (
                  <RoundedImage 
                    src="/images/bot.png" 
                    alt="Emergent" 
                    width={16} 
                    height={16} 
                    sx={{ marginRight: 4 }} 
                  />
                )}
                {type.charAt(0).toUpperCase()}
              </FilterButton>
            ))}
          </Box>
        </Box>
        
        <Divider sx={{ borderColor: 'rgba(138, 43, 226, 0.2)', mb: 2 }} />
        
        {filteredContributions.map((contribution) => (
          <ContributionItem key={contribution.id}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#fff' }}>
                {contribution.title}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {new Date(contribution.date).toLocaleDateString()}
              </Typography>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              backgroundColor: getContributionTypeColor(contribution.type),
              color: '#fff',
              borderRadius: '12px',
              padding: '2px 8px',
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}>
              +{contribution.points}
            </Box>
          </ContributionItem>
        ))}
        
        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#8A2BE2', 
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            View All Contributions
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default UserContributionSidebar; 